import {
  Col,
  Form,
  Input,
  Row,
  type FormInstance,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { useCallback, useEffect, useMemo } from "react";
import {
  createRetailerKeyRules,
  createRetailerNameRules,
} from "./retailerKeyValidation";

export interface RetailerAddEditFormValues {
  retailerName: string;
  retailerKey: string;
}

interface MasterDataRetailerAddEditFormProps {
  form: FormInstance<RetailerAddEditFormValues>;
  mode: "create" | "edit";
  initialValues: RetailerAddEditFormValues;
  onSubmit: (values: RetailerAddEditFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
}

export const MasterDataRetailerAddEditForm = ({
  form,
  mode,
  initialValues,
  onSubmit,
  onFormValidityChange,
}: MasterDataRetailerAddEditFormProps) => {
  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);

    const values = form.getFieldsValue();
    const retailerName = values.retailerName?.trim() ?? "";
    const retailerKey = values.retailerKey?.trim() ?? "";
    const hasRequiredValues =
      retailerName !== "" && (mode === "edit" || retailerKey !== "");

    const isDirty =
      mode === "create"
        ? true
        : retailerName !== (initialValues.retailerName?.trim() ?? "");

    onFormValidityChange?.(hasRequiredValues && !hasErrors && isDirty);
  }, [form, initialValues, mode, onFormValidityChange]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  const nameRules = useMemo(() => createRetailerNameRules(), []);
  const keyRules = useMemo(() => createRetailerKeyRules(), []);

  return (
    <ErrorBoundary>
      <Form
        form={form}
        onFinish={onSubmit}
        onFieldsChange={updateFormValidity}
        className="master-data-retailer-add-edit"
      >
        <Row>
          <Col span={6}>
            <Form.Item
              name="retailerName"
              label="Retailer Name"
              validateTrigger={["onChange", "onBlur"]}
              rules={nameRules}
            >
              <Input placeholder="Retailer name" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="retailerKey"
              label="Retailer Key"
              validateTrigger={["onChange", "onBlur"]}
              rules={mode === "create" ? keyRules : []}
            >
              <Input
                placeholder="Retailer key"
                isDisabled={mode === "edit"}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ErrorBoundary>
  );
};
